<template>
  <div class="about1-wrap">
    <main class="page">

      <p class="breadcrumb">{{ $t('about1.breadcrumb') }}</p>

      <h1 class="page-title">{{ $t('about1.title') }}</h1>
      <p class="page-subtitle">{{ $t('about1.subtitle') }}</p>

      <div class="meta-strip">
        <div v-for="m in meta" :key="m.id" class="meta-item">
          <span class="meta-label">{{ $t(m.labelKey) }}</span>
          <span class="meta-value">{{ $t(m.valueKey) }}</span>
        </div>
      </div>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.situation.title') }}</h2>
        <p class="section-text">{{ $t('about1.sections.situation.body') }}</p>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.courseOfAction.title') }}</h2>
        <p class="section-text">{{ $t('about1.sections.courseOfAction.body') }}</p>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.lookingAhead.title') }}</h2>
        <p class="section-text">{{ $t('about1.sections.lookingAhead.body') }}</p>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.sdgs.title') }}</h2>
        <p class="section-text">{{ $t('about1.sections.sdgs.body') }}</p>
        <div class="sdg-row">
          <div v-for="sdg in sdgs" :key="sdg.number" class="sdg-card">
            <div
              class="sdg-tile"
              :class="`sdg-${sdg.number}`"
              :aria-label="`SDG ${sdg.number}: ${$t(sdg.textKey)}`"
            >
              <span class="sdg-number">{{ sdg.number }}</span>
              <span class="sdg-tile-text">{{ $t(sdg.textKey) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.team.title') }}</h2>
        <div v-for="group in teamGroups" :key="group.titleKey" class="team-group">
          <p class="team-group-title">{{ $t(group.titleKey) }}</p>
          <ul class="team-list">
            <li v-for="m in group.members" :key="m.name" class="team-member">
              <p class="team-name">{{ m.name }}</p>
              <p class="team-role">{{ $t(m.roleKey) }}</p>
            </li>
          </ul>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.privacy.title') }}</h2>
        <div v-for="card in privacyCards" :key="card.id" class="info-card">
          <p class="info-card-label">{{ $t(card.labelKey) }}</p>
          <p class="info-card-text">{{ $t(card.textKey) }}</p>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.funding.title') }}</h2>
        <p class="section-text">{{ $t('about1.sections.funding.body') }}</p>
        <div class="funding-row">
          <a
            class="funding-tag"
            href="https://www.bfh.ch/en/"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('about1.fundingTags.bfh') }}</a>
          <a
            class="funding-tag"
            href="https://www.blv.admin.ch/blv/en/home.html"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('about1.fundingTags.blv') }}</a>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.contact.title') }}</h2>
        <p class="section-text">
          {{ $t('about1.contact.headLabel') }}
          <a
            href="https://www.bfh.ch/en/about-bfh/people/5atyfgtzmrx3/"
            target="_blank"
            rel="noopener noreferrer"
          >Prof. Dr. Tamara Bucher</a>,
          {{ $t('about1.contact.headRole') }}, +41 31 848 67 71.
        </p>
        <p class="section-text">
          {{ $t('about1.contact.pageLabel') }}
          <a
            href="https://www.bfh.ch/en/research/research-projects/2025-243-987-410/"
            target="_blank"
            rel="noopener noreferrer"
          >bfh.ch/research-projects/2025-243-987-410</a>
        </p>
      </section>

      <section class="section">
        <h2 class="section-title">{{ $t('about1.sections.revisionHistory.title') }}</h2>
        <ol class="timeline">
          <li
            v-for="(m, i) in milestones"
            :key="m.date"
            class="timeline-item"
            :class="{ 'is-current': i === milestones.length - 1 }"
          >
            <span class="timeline-date">{{ m.date }}</span>
            <span class="timeline-label">{{ localized(m.label) }}</span>
          </li>
        </ol>
      </section>

      <div class="footer">
        <span>{{ $t('about1.footer.copyright') }}</span>
        <span>{{ $t('about1.footer.lastUpdated') }}</span>
      </div>

    </main>
  </div>
</template>

<script>
export default {
  name: 'About1',
  data() {
    return {
      meta: [
        { id: 'institution', labelKey: 'about1.meta.institution.label', valueKey: 'about1.meta.institution.value' },
        { id: 'school', labelKey: 'about1.meta.school.label', valueKey: 'about1.meta.school.value' },
        { id: 'institute', labelKey: 'about1.meta.institute.label', valueKey: 'about1.meta.institute.value' },
        { id: 'duration', labelKey: 'about1.meta.duration.label', valueKey: 'about1.meta.duration.value' },
        { id: 'status', labelKey: 'about1.meta.status.label', valueKey: 'about1.meta.status.value' },
      ],
      sdgs: [
        { number: 3, textKey: 'about1.sdgList.3' },
        { number: 11, textKey: 'about1.sdgList.11' },
        { number: 12, textKey: 'about1.sdgList.12' },
      ],
      teamGroups: [
        {
          titleKey: 'about1.teamGroups.leadership',
          members: [
            { name: 'Tamara Bucher', roleKey: 'about1.roles.bucher' },
            { name: 'Sandra Jent', roleKey: 'about1.roles.jent' },
          ],
        },
        {
          titleKey: 'about1.teamGroups.advisor',
          members: [
            { name: 'Clare Collins', roleKey: 'about1.roles.collins' },
          ],
        },
        {
          titleKey: 'about1.teamGroups.concept',
          members: [
            { name: 'David Fäh', roleKey: 'about1.roles.faeh' },
            { name: 'Matthias Meier', roleKey: 'about1.roles.meier' },
            { name: 'Anja Raab', roleKey: 'about1.roles.raab' },
            { name: 'Joyce Haddad', roleKey: 'about1.roles.haddad' },
            { name: 'Elisa Bertozzi', roleKey: 'about1.roles.bertozzi' },
            { name: 'Edona Idrizi', roleKey: 'about1.roles.idrizi' },
            { name: 'Remo Fischer', roleKey: 'about1.roles.fischer' },
            { name: 'Ines Stalder', roleKey: 'about1.roles.stalder' },
          ],
        },
        {
          titleKey: 'about1.teamGroups.digital',
          members: [
            { name: 'Ken Ritley', roleKey: 'about1.roles.ritley' },
            { name: 'Shakir Sultanov', roleKey: 'about1.roles.sultanov' },
          ],
        },
      ],
      privacyCards: [
        { id: 'privacy', labelKey: 'about1.privacyCards.privacy.label', textKey: 'about1.privacyCards.privacy.text' },
        { id: 'ethics', labelKey: 'about1.privacyCards.ethics.label', textKey: 'about1.privacyCards.ethics.text' },
        { id: 'minors', labelKey: 'about1.privacyCards.minors.label', textKey: 'about1.privacyCards.minors.text' },
      ],

      // ─── Revision history ────────────────────────────────────────
      //
      // Milestones live here as inline {de,fr,it,en} objects rather than
      // as locale-file keys like everything else on this page. That is a
      // deliberate exception: the list grows every few weeks, and one
      // object in one file beats four JSON edits per entry. The shape
      // matches the i18n_text type used for questionnaire content
      // (docs/schemas/_types/i18n_text.md), so it is not a new idea.
      //
      // TO ADD A MILESTONE: append one object below. Oldest first; the
      // last entry is highlighted automatically as "where we are now".
      // English alone is enough; localized() falls back rather than
      // showing a gap, so a missing translation degrades quietly.
      milestones: [
        {
          date: '03.05.2026',
          label: {
            en: 'Initial setup',
            de: 'Ersteinrichtung',
            fr: 'Configuration initiale',
            it: 'Configurazione iniziale',
          },
        },
        {
          date: '10.05.2026',
          label: {
            en: 'First basic functionality',
            de: 'Erste Grundfunktionen',
            fr: 'Premières fonctionnalités de base',
            it: 'Prime funzionalità di base',
          },
        },
        {
          date: '31.05.2026',
          label: {
            en: 'First version for team demo',
            de: 'Erste Version für die Team-Demo',
            fr: "Première version pour la démonstration à l'équipe",
            it: 'Prima versione per la demo al team',
          },
        },
        {
          date: '29.07.2026',
          label: {
            en: 'Incorporate feedback from workshop with Claire',
            de: 'Feedback aus dem Workshop mit Claire eingearbeitet',
            fr: "Intégration des retours de l'atelier avec Claire",
            it: 'Integrazione dei riscontri dal workshop con Claire',
          },
        },
        {
          date: '09.08.2026',
          label: {
            en: 'Retrieving data',
            de: 'Datenabruf',
            fr: 'Récupération des données',
            it: 'Recupero dei dati',
          },
        },
        {
          date: '02.09.2026',
          label: {
            en: 'New quiz loaded for the summer workshops',
            de: 'Neues Quiz für die Sommer-Workshops geladen',
            fr: "Nouveau quiz chargé pour les ateliers d'été",
            it: 'Nuovo quiz caricato per i workshop estivi',
          },
        },
        {
          date: '11.09.2026',
          label: {
            en: 'September version of the quiz loaded',
            de: 'September-Version des Quiz geladen',
            fr: 'Version de septembre du quiz chargée',
            it: 'Versione di settembre del quiz caricata',
          },
        },
      ],
    };
  },
  methods: {
    /**
     * Resolve an inline {de,fr,it,en} object for the active locale.
     * Same fallback chain the quiz runtime uses: active locale, then
     * English, then any non-empty value, then empty string. Never
     * renders a placeholder or a raw key. A missing translation should
     * look like nothing happened, not like a bug.
     */
    localized(dict) {
      if (!dict || typeof dict !== 'object') return '';
      const order = [this.$i18n.locale, 'en', 'de', 'fr', 'it'];
      for (const loc of order) {
        const v = dict[loc];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    },
  },
};
</script>

<style scoped>
.about1-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #42b983;
  --color-accent-dark: #2E7D32;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.about1-wrap *,
.about1-wrap *::before,
.about1-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 820px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0 0 2rem;
  font-weight: 400;
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.section-text {
  font-size: 15px;
  color: var(--color-text-primary);
  line-height: 1.7;
  margin: 0 0 1rem;
}

.section-text:last-child { margin-bottom: 0; }

.section-text a {
  color: var(--color-accent-dark);
  text-decoration: none;
  border-bottom: 1px solid rgba(46, 125, 50, 0.3);
}

.section-text a:hover {
  border-bottom-color: var(--color-accent-dark);
}

.team-group {
  margin-bottom: 1.75rem;
}

.team-group:last-child { margin-bottom: 0; }

.team-group-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent-dark);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 0.75rem;
}

.team-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 0.5px solid var(--color-border);
}

.team-member {
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
  padding: 14px 0;
  border-bottom: 0.5px solid var(--color-border);
}

@media (min-width: 600px) {
  .team-member {
    grid-template-columns: 200px 1fr;
    gap: 16px;
    align-items: baseline;
  }
}

.team-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.team-role {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.info-card {
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  padding: 16px 20px;
  margin-bottom: 1rem;
}

.info-card-label {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  margin: 0 0 6px;
}

.info-card-text {
  font-size: 14px;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.6;
}

.funding-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.funding-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--color-bg-soft);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-text-primary);
  font-weight: 500;
  text-decoration: none;
}

.funding-tag:hover {
  background: var(--color-bg-muted);
  border-color: var(--color-border-strong);
}

.sdg-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 0.5rem;
}

.sdg-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 130px;
}

.sdg-tile {
  width: 110px;
  height: 110px;
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  text-align: center;
  padding: 12px 10px;
  line-height: 1.2;
}

.sdg-3 { background: #4C9F38; }
.sdg-11 { background: #FD9D24; }
.sdg-12 { background: #BF8B2E; }

.sdg-number {
  font-size: 32px;
  line-height: 1;
  margin-bottom: 6px;
}

.sdg-tile-text {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ── Revision history timeline ─────────────────────────────────────
   A vertical rail with a dot per milestone. Vertical rather than
   horizontal because this app is phone-first: the layout narrows
   gracefully instead of forcing a sideways scroll. The last entry
   carries .is-current, giving it a filled dot with a halo so "where the
   project stands today" reads at a glance. */
.timeline {
  list-style: none;
  position: relative;
  max-width: 640px;
  margin: 0;
  padding: 0.25rem 0 0 30px;
}

/* The rail. Fades at both ends so it reads as an ongoing story rather
   than a bar with hard stops. */
.timeline::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 10px;
  bottom: 10px;
  width: 2px;
  border-radius: 1px;
  background: linear-gradient(
    to bottom,
    rgba(66, 185, 131, 0.15),
    rgba(66, 185, 131, 0.55) 12%,
    rgba(66, 185, 131, 0.55) 88%,
    rgba(66, 185, 131, 0.15)
  );
}

.timeline-item {
  position: relative;
  padding: 0 0 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

/* Dot centred on the rail: the rail spans x = 6..8 (centre 7), and the
   14px dot sits at the list's padding edge, so its centre lands on 7. */
.timeline-item::before {
  content: "";
  position: absolute;
  left: -30px;
  top: 5px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--color-bg-page);
  border: 2.5px solid var(--color-accent);
}

.timeline-item.is-current::before {
  background: var(--color-accent);
  box-shadow: 0 0 0 5px rgba(66, 185, 131, 0.16);
}

.timeline-date {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-accent-dark);
  font-variant-numeric: tabular-nums;
}

.timeline-label {
  font-size: 15px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.timeline-item.is-current .timeline-label {
  color: var(--color-text-primary);
  font-weight: 600;
}

@media (max-width: 480px) {
  .timeline { padding-left: 26px; }
  .timeline::before { left: 5px; }
  .timeline-item::before {
    left: -26px;
    width: 12px;
    height: 12px;
    top: 6px;
  }
  .timeline-label { font-size: 14px; }
}

.footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 0.5px solid var(--color-border);
  font-size: 12px;
  color: var(--color-text-tertiary);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}
</style>
